import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Check, ArrowRight, Zap, Star, Activity,
  Shield, Clock, LogOut, Loader2, Database
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrialExpiredPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const bg = language === "bg";
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [freeLoading, setFreeLoading] = useState(false);

  const sub = user?.subscription || {};
  const trialEnd = sub.trial_end ? new Date(sub.trial_end).toLocaleDateString(bg ? "bg-BG" : "en-US", { day: "numeric", month: "long", year: "numeric" }) : "";

  const handleCheckout = async (planId) => {
    // iter690: Stripe премахнат — навигираме към Pricing modal (Wise)
    setCheckoutLoading(planId);
    try {
      navigate(`/pricing?plan=${planId}`);
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleContinueFree = async () => {
    setFreeLoading(true);
    try {
      await axios.post(`${API}/subscription/continue-free`);
      await refreshUser();
      toast.success(bg ? "Продължавате с безплатен план" : "You're now on the Free plan");
      navigate("/dashboard");
    } catch (e) {
      toast.error(bg ? "Грешка" : "Error");
    } finally {
      setFreeLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className={`min-h-screen ${isDark ? "bg-background" : "bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4]"}`} data-testid="trial-expired-page">
      {/* Header */}
      <header className={`sticky top-0 z-40 backdrop-blur-xl border-b ${isDark ? "bg-[hsl(223,47%,8%)]/90 border-[hsl(223,47%,15%)]" : "bg-white/90 border-gray-100"}`}>
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/app-icon.png" alt="Nutramilo" className="h-9 w-9 object-contain sm:hidden" />
            <img src="/nutramilo-logo.png" alt="Nutramilo" className="hidden sm:block h-8 w-auto object-contain" onError={e => e.target.style.display = "none"} />
          </div>
          <button onClick={handleLogout} className={`flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg ${isDark ? "text-gray-400 hover:bg-[hsl(223,47%,15%)]" : "text-gray-500 hover:bg-gray-100"}`} data-testid="logout-btn">
            <LogOut className="w-3.5 h-3.5" /> {bg ? "Изход" : "Logout"}
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 sm:py-14">
        {/* Trial Ended Banner */}
        <div className="text-center mb-10">
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full mb-5 ${isDark ? "bg-amber-500/10 text-amber-400" : "bg-amber-50 text-amber-600"}`}>
            <Clock className="w-4 h-4" />
            <span className="text-sm font-medium">
              {bg ? "Пробният ви период приключи" : "Your trial has ended"}
              {trialEnd && ` — ${trialEnd}`}
            </span>
          </div>
          <h1 className={`text-2xl sm:text-3xl lg:text-4xl font-bold mb-3 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
            {bg ? "Продължете вашия метаболитен път" : "Continue Your Metabolic Journey"}
          </h1>
          <p className={`text-sm sm:text-base max-w-lg mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
            {bg
              ? "Вече имате персонализиран метаболитен профил. Изберете план, за да продължите да получавате AI насоки."
              : "You already have a personalized metabolic profile. Choose a plan to keep receiving AI guidance."}
          </p>
        </div>

        {/* Plans — Free option + link to pricing */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 max-w-2xl mx-auto">
          {/* Free Plan */}
          <Card className={`relative overflow-hidden transition-all hover:shadow-lg ${isDark ? "bg-card border-border" : "border-gray-100"}`} data-testid="free-plan-card">
            <CardContent className="p-5 sm:p-6">
              <div className="flex items-center gap-2 mb-2">
                <Database className="w-5 h-5 text-[#64748B]" />
                <h3 className={`text-base font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                  {bg ? "Безплатен" : "Free"}
                </h3>
              </div>
              <div className="flex items-baseline gap-1 mb-5">
                <span className={`text-4xl font-bold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>0</span>
                <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}>EUR</span>
              </div>
              <ul className="space-y-2.5 mb-6">
                {(bg ? [
                  "Хранителен дневник",
                  "Проследяване на макроси",
                  "Здравни калкулатори",
                  "Научна библиотека",
                ] : [
                  "Meal diary",
                  "Macro tracking",
                  "Health calculators",
                  "Science library",
                ]).map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-[#64748B] mt-0.5 flex-shrink-0" />
                    <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="outline"
                onClick={handleContinueFree}
                disabled={freeLoading}
                className={`w-full h-12 rounded-xl text-sm font-semibold ${isDark ? "border-[hsl(223,47%,22%)] text-gray-300" : ""}`}
                data-testid="continue-free-btn"
              >
                {freeLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {bg ? "Продължете безплатно" : "Continue Free"}
              </Button>
            </CardContent>
          </Card>

          {/* Upgrade CTA */}
          <Card className={`relative overflow-hidden transition-all hover:shadow-xl ${isDark ? "bg-card border-[#8B5CF6]/30" : "border-[#8B5CF6]/20 shadow-lg"} border-2 flex flex-col justify-center`} data-testid="upgrade-plans-card">
            <div className="h-1.5 bg-gradient-to-r from-[#0EA5E9] via-[#8B5CF6] to-[#EC4899]" />
            <CardContent className="p-5 sm:p-6 text-center">
              <Star className="w-8 h-8 text-[#8B5CF6] mx-auto mb-3" />
              <h3 className={`text-base font-bold mb-2 ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>
                {bg ? "Надградете за пълен достъп" : "Upgrade for full access"}
              </h3>
              <p className={`text-sm mb-5 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                {bg ? "AI анализи, хранителни планове, нутригеномика и клиничен преглед" : "AI analyses, meal plans, nutrigenomics and clinical review"}
              </p>
              <p className={`text-xs mb-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                {bg ? "Планове от €29/мес" : "Plans from €29/mo"}
              </p>
              <Link to="/pricing">
                <Button className="bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] text-white w-full h-12 rounded-xl text-sm font-semibold" data-testid="view-plans-btn">
                  {bg ? "Вижте плановете" : "View Plans"} <ArrowRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>

        {/* Reassurance Section */}
        <div className={`grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8`}>
          {[
            { icon: Shield, color: "#10B981", title_bg: "Без дълъг ангажимент", title_en: "No long commitment", desc_bg: "Откажете по всяко време", desc_en: "Cancel anytime" },
            { icon: Zap, color: "#F59E0B", title_bg: "Данните ви са запазени", title_en: "Your data is saved", desc_bg: "Профилът ви остава активен", desc_en: "Your profile stays active" },
            { icon: Activity, color: "#0EA5E9", title_bg: "Продължете от там", title_en: "Continue where you left", desc_bg: "Без нужда от нова оценка", desc_en: "No new assessment needed" },
          ].map(item => (
            <div key={item.title_en} className={`flex items-center gap-3 p-4 rounded-xl ${isDark ? "bg-[hsl(223,47%,10%)]" : "bg-white shadow-sm"}`}>
              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${item.color}15` }}>
                <item.icon className="w-5 h-5" style={{ color: item.color }} />
              </div>
              <div>
                <p className={`text-sm font-semibold ${isDark ? "text-white" : "text-[#1A1A2E]"}`}>{bg ? item.title_bg : item.title_en}</p>
                <p className={`text-xs ${isDark ? "text-gray-500" : "text-gray-400"}`}>{bg ? item.desc_bg : item.desc_en}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Contact */}
        <div className={`text-center py-6 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
          <p className="text-xs">
            {bg ? "Имате въпроси? " : "Have questions? "}
            <button onClick={() => navigate("/contact")} className="text-[#0EA5E9] font-medium hover:underline">
              {bg ? "Свържете се с нас" : "Contact us"}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
